import * as React from "react";
import { shallowEqual } from "react-redux";
import { Routes as RouterRoutes, Route, useLocation, Navigate } from "react-router-dom";

import { LoaderContainer } from "./components/Utilities/LoaderContainer";

import { lazyWithRetry } from "./hooks/lazyWithRetry";
import { NioxFenoKioskRoute } from "./libs/niox-feno/constants";
import { getCurrentOrganization } from "./libs/dataLib";
import { useAppSelector } from "./redux/store";
import OscillometryDetailedView from "./containers/Trials/Views/OscillometryDetailedView";

const { Suspense } = React;

const Home = lazyWithRetry(() => import("./containers/Home/Home"));
const Login = lazyWithRetry(() => import("./containers/Login"));
const Patient = lazyWithRetry(() => import("./containers/Patient"));
const NotFound = lazyWithRetry(() => import("./containers/NotFound"));
const Providers = lazyWithRetry(() => import("./containers/Providers"));
const ForgotPassword = lazyWithRetry(() => import("./containers/ForgotPassword"));
const SamlLogin = lazyWithRetry(() => import("./containers/SamlLogin"));
const OneHomeLogin = lazyWithRetry(() => import("./containers/OneHomeLogin"));
const Signup = lazyWithRetry(() => import("./containers/Signup"));
const MyAccount = lazyWithRetry(() => import("./containers/MyAccount"));
const Admin = lazyWithRetry(() => import("./containers/Admin"));
const Organization = lazyWithRetry(() => import("./containers/Organization"));
const EditOrganization = lazyWithRetry(() => import("./containers/EditOrganization"));
const Reports = lazyWithRetry(() => import("./containers/Reports"));
const Kiosk = lazyWithRetry(() => import("./containers/Kiosk/Kiosk"));
const Session = lazyWithRetry(() => import("./containers/Kiosk/Session"));
const Management = lazyWithRetry(() => import("./containers/Kiosk/Management"));
const Calibration = lazyWithRetry(() => import("./containers/Kiosk/Calibration"));
const MobileDeviceNotFound = lazyWithRetry(() => import("./containers/Kiosk/MobileDeviceNotFound"));
const NioxFeno = lazyWithRetry(() => import("./containers/Kiosk/NioxFeno"));
const Oscillometry = lazyWithRetry(() => import("./containers/Kiosk/Oscillometry"));

interface RoutesProps {
  appProps: {
    handleLogout: () => void;
    logoutIntervalMinutes: React.MutableRefObject<number>;
    samlError: string;
  };
};

export const Routes: React.FC<RoutesProps> = ({ appProps }) => {
  const location = useLocation();
  const { isKioskMode, } = useAppSelector(state => state.kiosk);
  const provider = useAppSelector(state => state.provider, shallowEqual);
  const { detailedOrganizationsForProvider } = useAppSelector(state => state.organizationsForProvider);
  const { detailedOrganizations } = useAppSelector(state => state.organizations);
  const isAuthenticated = !!provider.providerID;
  const organization = isAuthenticated ? getCurrentOrganization(provider.viewingAs, provider.fullAdmin ? detailedOrganizations : detailedOrganizationsForProvider) : undefined;
  const isOverreadingOrg = !!organization?.featureSet?.isOverreading;
  const isOrgAdmin = !!provider.viewingAs?.orgAdmin || !!provider.fullAdmin;
  const needsPolicyRedirect = isAuthenticated && !provider.hasAgreedToPrivacyPolicy;

  /*
   * Example flow for Kiosk load:
   * 1. Mobile navigates to /kiosk?kioskID=... unauthenticated, redirectElement used, redirectTo = /login?redirect=%2Fkiosk%3FkioskID%3D..., land on /login.
   * 2. Re-render: isOnAuthPath is true, redirectTo becomes bare /login, redirect is read and decoded as /kiosk?kioskID=... and passed to unauthenticatedRedirectElement.
   * 3. When the user authenticates we are still in the Login component, isAuthenticated becomes true and re-renders, and navigates to unauthenticatedRedirectElement, i.e. /kiosk?kioskID=...
   * - NOTE: Important that redirect is pulled from react-router-dom location as on re-render window.location will be ahead of React state, causing
   * redirect to not be present in the query string (at this moment its ?kioskID=...), causing redirect to be null, unauthenticatedRedirectElement
   * to navigate to just /kiosk, and the kiosk unable to fetch the data (hits recovery flow).
   */

  // Don't build a login-redirect that points back at an auth page,
  // and encode the return path so the query string survives intact.
  const authPaths = ["/login", "/saml", "/forgot-password", "/signup"];
  const isOnAuthPath = authPaths.some(p => location.pathname.startsWith(p));
  const safeReturnPath = `${location.pathname}${location.search}`;
  const redirectTo = isOnAuthPath
    ? "/login"
    : `/login?redirect=${encodeURIComponent(safeReturnPath)}`;

  const redirect = new URLSearchParams(location.search).get("redirect");
  const unauthenticatedRedirectTo = redirect === "" || redirect === null ? `/${isKioskMode ? 'kiosk' : ''}` : redirect;
  const redirectElement = <Navigate replace to={redirectTo} />;
  const notOrgAdminRedirectTo = <Navigate replace to="/" />;
  const homeRedirectElement = <Navigate replace to="/" />;
  const unauthenticatedRedirectElement = <Navigate replace to={unauthenticatedRedirectTo} />;

  const privacyPolicyRedirectHelper = (redirect: JSX.Element) => {
    if (needsPolicyRedirect) return <Navigate replace to="/" />;
    return redirect;
  };

  return (
    <Suspense fallback={<LoaderContainer />}>
      <RouterRoutes>
        {/******** Unauthenticated Routes ********/}
        <Route path="/login" element={!isAuthenticated ? <Login {...appProps} /> : unauthenticatedRedirectElement} />
        <Route path="/saml/:email" element={!isAuthenticated ? <SamlLogin {...appProps} /> : unauthenticatedRedirectElement} />
        <Route path="/saml" element={!isAuthenticated ? <SamlLogin {...appProps} /> : unauthenticatedRedirectElement} />
        <Route path="/onehome" element={!isAuthenticated ? <OneHomeLogin {...appProps} /> : unauthenticatedRedirectElement} />
        <Route path="/forgot-password" element={!isAuthenticated ? <ForgotPassword {...appProps} /> : unauthenticatedRedirectElement} />
        <Route path="/signup/:email/:code" element={!isAuthenticated ? <Signup {...appProps} /> : unauthenticatedRedirectElement} />

        {/******** Authenticated Routes ********/}
        {isKioskMode
          ? (<>
            <Route path="/" element={isAuthenticated ? <Kiosk {...appProps} /> : redirectElement} />
            <Route path="/mobile-not-found" element={isAuthenticated && !needsPolicyRedirect ? <MobileDeviceNotFound {...appProps} /> : privacyPolicyRedirectHelper(redirectElement)} />
            <Route path="/kiosk/:patientID/:kioskSessionID" element={isAuthenticated && !needsPolicyRedirect ? <Session {...appProps} /> : privacyPolicyRedirectHelper(redirectElement)} />
            <Route path="/kiosk" element={isAuthenticated && !needsPolicyRedirect ? <Kiosk {...appProps} /> : privacyPolicyRedirectHelper(redirectElement)} />
            <Route path="/kiosk/manage" element={isAuthenticated && !needsPolicyRedirect ? <Management {...appProps} /> : privacyPolicyRedirectHelper(notOrgAdminRedirectTo)} />
            <Route path="/calibration" element={isAuthenticated && !needsPolicyRedirect ? <Calibration {...appProps} /> : privacyPolicyRedirectHelper(redirectElement)} />
            <Route path="/kiosk/account" element={isAuthenticated && !needsPolicyRedirect ? <MyAccount {...appProps} /> : privacyPolicyRedirectHelper(redirectElement)} />
            <Route path={NioxFenoKioskRoute} element={isAuthenticated && !needsPolicyRedirect ? <NioxFeno {...appProps} /> : privacyPolicyRedirectHelper(redirectElement)} />
            <Route path="/kiosk/oscillometry" element={isAuthenticated && !needsPolicyRedirect ? <Oscillometry {...appProps} /> : privacyPolicyRedirectHelper(redirectElement)} />
          </>)
          : (<>
            <Route path="/" element={isAuthenticated ? <Home {...appProps} /> : redirectElement} />
            <Route path="/patient/:id" element={isAuthenticated && !needsPolicyRedirect ? <Patient {...appProps} /> : privacyPolicyRedirectHelper(redirectElement)} />
            <Route path="/patient/:id/:pftType/:seriesID" element={isAuthenticated && !needsPolicyRedirect ? <Patient {...appProps} /> : privacyPolicyRedirectHelper(redirectElement)} />
            <Route path="/participant/:id/osc/:measurementID" element={isAuthenticated && !needsPolicyRedirect ? <OscillometryDetailedView {...appProps} /> : privacyPolicyRedirectHelper(redirectElement)} />
            <Route path="/organization" element={isAuthenticated && !needsPolicyRedirect ? <Organization {...appProps} /> : privacyPolicyRedirectHelper(redirectElement)} />
            <Route path="/organization/:organizationID" element={isAuthenticated && !needsPolicyRedirect ? <EditOrganization {...appProps} /> : privacyPolicyRedirectHelper(redirectElement)} />
            <Route path="/reports" element={!isAuthenticated ? redirectElement : needsPolicyRedirect || isOverreadingOrg ? homeRedirectElement : <Reports {...appProps} />} />
            <Route path="/providers" element={isAuthenticated && !needsPolicyRedirect && isOrgAdmin ? <Providers {...appProps} /> : privacyPolicyRedirectHelper(notOrgAdminRedirectTo)} />
            <Route path="/admin" element={isAuthenticated && !needsPolicyRedirect && isOrgAdmin ? <Admin {...appProps} /> : privacyPolicyRedirectHelper(notOrgAdminRedirectTo)} />
            <Route path="/account" element={isAuthenticated && !needsPolicyRedirect ? <MyAccount {...appProps} /> : privacyPolicyRedirectHelper(redirectElement)} />
          </>)}

        <Route path="*" element={<NotFound />} />
      </RouterRoutes>
    </Suspense>
  );
};